import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui/components/ui/table"
import { cn } from "@repo/ui/lib/utils"
import { Table as TanstackTable, flexRender } from "@tanstack/react-table"
import * as React from "react"
import { useTranslations } from "use-intl"
import DataTablePagination from "./data-table-pagination"

interface DataTableProps<TData> extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The table instance returned from useDataTable hook with pagination, sorting, filtering, etc.
   * @type TanstackTable<TData>
   */
  table: TanstackTable<TData>

  /**
   * The floating bar to render at the bottom of the table on row selection.
   * @default null
   * @type React.ReactNode | null
   * @example floatingBar={<TasksTableFloatingBar table={table} />}
   */
  floatingBar?: React.ReactNode | null
  /**
   * The function to call when a row is clicked.
   * @default undefined
   * @type (row: TData, event: React.MouseEvent) => void | undefined
   * @example onRowClick={(row) => console.log(row)}
   */
  onRowClick?: (row: TData, event: React.MouseEvent) => void
  /** Called on non-primary clicks (e.g. middle-click) on a row. */
  onRowAuxClick?: (row: TData, event: React.MouseEvent) => void
  /** Extra classes for the bordered box around the table itself. */
  tableContainerClassName?: string
}

function DataTable<TData>({
  table,
  floatingBar = null,
  onRowClick,
  onRowAuxClick,
  tableContainerClassName,
  children,
  className,
  ...props
}: DataTableProps<TData>) {
  const t = useTranslations("ui.common")
  return (
    <div className={cn("w-full min-w-0 space-y-2.5", className)} {...props}>
      {children}
      <div
        className={cn(
          "overflow-x-auto rounded-md border",
          tableContainerClassName,
        )}
      >
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const isSticky = header.column.columnDef.meta?.sticky === true
                  return (
                    <TableHead
                      key={header.id}
                      colSpan={header.colSpan}
                      className={cn(
                        isSticky &&
                          "sticky right-0 bg-background shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.1)]",
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className={cn(
                    "group/row",
                    table.options.enableRowSelection &&
                      "cursor-pointer hover:bg-muted",
                    onRowClick && "cursor-pointer",
                  )}
                  onClick={(event) => {
                    row.toggleSelected()
                    onRowClick?.(row.original, event)
                  }}
                  onAuxClick={(event) => onRowAuxClick?.(row.original, event)}
                >
                  {row.getVisibleCells().map((cell) => {
                    const isSticky = cell.column.columnDef.meta?.sticky === true
                    return (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          isSticky &&
                            "sticky right-0 bg-background shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.1)]",
                        )}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={table.getAllColumns().length}
                  className="h-24 text-center"
                >
                  {t("noResults")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex flex-col gap-2.5">
        <DataTablePagination table={table} />
        {table.getFilteredSelectedRowModel().rows.length > 0 && floatingBar}
      </div>
    </div>
  )
}

export default DataTable
